import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useWizard } from '../WizardProvider';
import WizardShell from '../WizardShell';
import MaskedInput from '../components/MaskedInput';
import { accountSchema, type AccountFormValues } from '../schemas/account.schema';
import { checkLenderIdAvailable, checkSalesforceIdUnique } from '../../../../api/lenderOnboarding';
import { SALESFORCE_ID_DUPLICATE } from '../copy';
import { deriveLenderId, isDerivedFrom, lenderIdVariant } from '../lenderId';
import { registerTrimmed } from '../trimOnBlur';
import { US_STATES } from '../usStates';

export default function Step1Account({ onExit }: { onExit: () => void }) {
  const { state, dispatch } = useWizard();
  const [duplicate, setDuplicate] = useState(false);
  const [checkedId, setCheckedId] = useState<string | null>(null);

  const { register, watch, setValue, formState: { errors, isValid } } =
    useForm<AccountFormValues>({
      resolver: zodResolver(accountSchema),
      mode: 'onChange',
      defaultValues: state.account,
    });

  // Every free-form box on this step trims when it loses focus (OM-4602). The State select and
  // the generated Lender ID are the only fields that cannot carry padding to begin with.
  const field = registerTrimmed(register, setValue);

  const values = watch();
  const salesforceId = values.salesforceId;
  const lenderName = values.lenderName;

  // The account identifier is generated, never typed or shown (OM-3918): derived from the name
  // — 10 lowercase alphanumerics — probed for availability, and silently stepped to a numeric
  // variant when taken. A settled id (this name's derivation or one of its variants) is left
  // alone: after a half-finished submit the lender already exists under it, and re-probing
  // would step onto a second account. Settledness is derived synchronously so Next never sees
  // the one frame where the form is valid but the id has not landed yet.
  const storedLenderId = state.account.lenderId;
  const idSettled = isDerivedFrom(storedLenderId, lenderName ?? '');
  useEffect(() => {
    const base = deriveLenderId(lenderName ?? '');
    if (!base) {
      if (storedLenderId) dispatch({ type: 'setAccount', data: { lenderId: '' } });
      return;
    }
    if (isDerivedFrom(storedLenderId, lenderName ?? '')) return;
    let cancelled = false;
    (async () => {
      let candidate = base;
      for (let attempt = 1; attempt <= 99; attempt++) {
        const { available } = await checkLenderIdAvailable(candidate);
        if (cancelled || available) break;
        candidate = lenderIdVariant(base, attempt);
      }
      if (!cancelled) dispatch({ type: 'setAccount', data: { lenderId: candidate } });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lenderName, storedLenderId]);

  // Commits to wizard state so navigating away and back preserves the data.
  useEffect(() => { dispatch({ type: 'setAccount', data: values }); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(values)]);

  useEffect(() => {
    let cancelled = false;
    if (!salesforceId || errors.salesforceId) {
      setDuplicate(false);
      setCheckedId(null);
      return;
    }
    checkSalesforceIdUnique(salesforceId)
      .then((unique) => { if (!cancelled) { setDuplicate(!unique); setCheckedId(salesforceId); } })
      .catch(() => { if (!cancelled) { setDuplicate(false); setCheckedId(salesforceId); } });
    return () => { cancelled = true; };
  }, [salesforceId, errors.salesforceId]);

  // True from the instant salesforceId becomes syntactically valid until the async duplicate
  // check for that exact value has landed — closes the window where isValid is already true but
  // duplicate hasn't been confirmed either way yet (a `checking` flag set inside the effect would
  // still leave one rendered frame with Next enabled; this is derived synchronously instead).
  const awaitingCheck = !!salesforceId && !errors.salesforceId && checkedId !== salesforceId;

  return (
    <WizardShell
      title="Lender Details"
      canAdvance={isValid && !duplicate && !awaitingCheck && idSettled}
      onExit={onExit}
    >
      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Primary Information</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="field">
            <label className="label" htmlFor="lenderName">
              Lender Name <span className="text-red-500">*</span>
            </label>
            <input id="lenderName" className="input" {...field('lenderName')} />
            {errors.lenderName && (
              <p className="text-sm text-red-500 mt-1">{errors.lenderName.message}</p>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor="salesforceId">Salesforce ID</label>
            <input id="salesforceId" className="input" {...field('salesforceId')} />
            {errors.salesforceId && (
              <p className="text-sm text-red-500 mt-1">{errors.salesforceId.message}</p>
            )}
            {!errors.salesforceId && duplicate && (
              <p className="text-sm text-red-500 mt-1">{SALESFORCE_ID_DUPLICATE}</p>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor="nmlsId">NMLS ID</label>
            <input id="nmlsId" className="input" {...field('nmlsId')} />
            {errors.nmlsId && (
              <p className="text-sm text-red-500 mt-1">{errors.nmlsId.message}</p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Address</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="field sm:col-span-3">
            <label className="label" htmlFor="street">
              Address <span className="text-red-500">*</span>
            </label>
            <input id="street" className="input" {...field('street')} />
            {errors.street && <p className="text-sm text-red-500 mt-1">{errors.street.message}</p>}
          </div>
          <div className="field">
            <label className="label" htmlFor="city">
              City <span className="text-red-500">*</span>
            </label>
            <input id="city" className="input" {...field('city')} />
            {errors.city && <p className="text-sm text-red-500 mt-1">{errors.city.message}</p>}
          </div>
          <div className="field">
            <label className="label" htmlFor="state">
              State <span className="text-red-500">*</span>
            </label>
            <select id="state" className="input" {...register('state')}>
              <option value="">Select a state</option>
              {US_STATES.map(({ code, name }) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            {errors.state && <p className="text-sm text-red-500 mt-1">{errors.state.message}</p>}
          </div>
          <div className="field">
            <label className="label" htmlFor="zip">
              Zip <span className="text-red-500">*</span>
            </label>
            {/* Capped at five characters, but not filtered to digits: OM-4362 AC3 wants a typed
                "+" or an interior space to surface the inline error rather than be silently
                dropped. Edge padding comes off on blur like every other box (OM-4602), though
                the five-character cap means a space typed here costs a digit either way. */}
            <input id="zip" className="input" maxLength={5} {...field('zip')} />
            {errors.zip && <p className="text-sm text-red-500 mt-1">{errors.zip.message}</p>}
          </div>
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">FC1 Credentials</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="field">
            <label className="label" htmlFor="entityId">Entity ID</label>
            <input id="entityId" className="input" {...field('entityId')} />
          </div>
          <div className="field">
            <label className="label" htmlFor="entityName">Entity Name</label>
            <input id="entityName" className="input" {...field('entityName')} />
          </div>
          <div className="field">
            <label className="label" htmlFor="apiUsername">API Username</label>
            <input id="apiUsername" className="input" {...field('apiUsername')} />
          </div>
          <MaskedInput
            id="apiPassword"
            label="API Password"
            value={values.apiPassword}
            onChange={(v) => setValue('apiPassword', v, { shouldValidate: true })}
          />
        </div>
      </section>
    </WizardShell>
  );
}
